import { useEffect, useState } from "react";

type Viewport = {
  // Stable layout viewport (what CSS layout is based on)
  layoutWidth: number;
  layoutHeight: number;

  // Visual viewport (what portion is currently visible; changes with bars/zoom/keyboard)
  visualWidth: number;
  visualHeight: number;
  offsetLeft: number;
  offsetTop: number;
  scale: number;

  isPortrait: boolean;
};

function readViewport(): Viewport {
  const docEl = document.documentElement;

  const layoutWidth = Math.round(docEl.clientWidth);
  const layoutHeight = Math.round(docEl.clientHeight);

  const vv = window.visualViewport;
  const visualWidth = Math.round(vv?.width ?? layoutWidth);
  const visualHeight = Math.round(vv?.height ?? layoutHeight);
  const offsetLeft = Math.round(vv?.offsetLeft ?? 0);
  const offsetTop = Math.round(vv?.offsetTop ?? 0);
  const scale = vv?.scale ?? 1;

  const isPortrait = layoutHeight >= layoutWidth;

  return {
    layoutWidth,
    layoutHeight,
    visualWidth,
    visualHeight,
    offsetLeft,
    offsetTop,
    scale,
    isPortrait,
  };
}

/**
 * Robust viewport measurement:
 * - layout viewport is stable for sizing UI
 * - visual viewport is useful for “what’s actually visible”
 * - double-rAF helps avoid transient rotate values (esp iOS Safari)
 */
export function useWindowViewport(): Viewport {
  const [vp, setVp] = useState<Viewport>(() => {
    if (typeof window === "undefined") {
      return {
        layoutWidth: 0,
        layoutHeight: 0,
        visualWidth: 0,
        visualHeight: 0,
        offsetLeft: 0,
        offsetTop: 0,
        scale: 1,
        isPortrait: true,
      };
    }
    return readViewport();
  });

  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;

    const update = () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);

      // 1st frame: read
      raf1 = requestAnimationFrame(() => {
        const a = readViewport();

        // 2nd frame: read again to let rotation/address bar settle
        raf2 = requestAnimationFrame(() => {
          const b = readViewport();

          // If we get a transient crazy value on rotation, prefer the second read.
          // (This is intentionally simple; it avoids “PiP suddenly huge/tiny”.)
          setVp(b.layoutWidth && b.layoutHeight ? b : a);
        });
      });
    };

    update();

    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", update, { passive: true });

    const vv = window.visualViewport;
    vv?.addEventListener("resize", update, { passive: true });
    vv?.addEventListener("scroll", update, { passive: true });

    const so: any = (screen as any)?.orientation;
    so?.addEventListener?.("change", update);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);

      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);

      vv?.removeEventListener("resize", update as any);
      vv?.removeEventListener("scroll", update as any);

      so?.removeEventListener?.("change", update);
    };
  }, []);

  return vp;
}